import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    OnInit,
    output,
    signal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Geometry } from 'geojson';
import { firstValueFrom } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { EntityAttributesComponent } from '@connect-services/client/components/entity-attributes/entity-attributes.component';
import { EntityAttributesInputComponent } from '@connect-services/client/components/entity-attributes-input/entity-attributes-input.component';
import { RelatedLayerListComponent } from '@connect-services/client/components/entity-detail-template/related-layer-list/related-layer-list.component';
import { SynopticActionItemComponent } from '@connect-services/client/components/entity-detail-template/synoptic-action-item/synoptic-action-item.component';
import { EntityLinkDocumentListComponent } from '@connect-services/client/components/entity-link-document-list/entity-link-document-list.component';
import { AttributeValue } from '@connect-services/client/components/table-entity-detail/abstract-entity-detail.component';
import { ButtonComponent } from '@connect-services/client/lib/components/button/button.component';
import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { EntityProperties } from '@connect-services/client/models/panel.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import {
    Column,
    EditAction,
    Layer,
    LayerRelation,
    LayersService,
    Subscription as ApiSubscription,
} from '@connect-services/client/sdk/api';
import { AssertService } from '@connect-services/client/services/assert.service';
import { ContentLayoutService } from '@connect-services/client/services/content-layout.service';
import { DocumentIdentifiers } from '@connect-services/client/services/document.service';
import { EditEntityDetailService } from '@connect-services/client/services/edit-entity-detail.service';
import { EditModeStateService } from '@connect-services/client/services/edit-mode-state.service';
import { EntityEditPanelGuardService } from '@connect-services/client/services/entity-edit-panel-guard.service';
import { FeatureService } from '@connect-services/client/services/feature.service';
import {
    GeometryEditPreview,
    GeometryEditPreviewService,
} from '@connect-services/client/services/geometry-edit-preview.service';
import { HasPermissionsService } from '@connect-services/client/services/has-permissions.service';
import { HomeService } from '@connect-services/client/services/home.service';
import { ActiveGeometryTypeEnum } from '@connect-services/client/services/map-creation-geom.service';
import { EntityLocation } from '@connect-services/client/services/map-draw.service';
import { MapEditGeomService } from '@connect-services/client/services/map-edit-geom.service';
import { EditModeType, MapEditModeService } from '@connect-services/client/services/map-edit-mode.service';
import { MapHighlightEntityService } from '@connect-services/client/services/map-highlight-entity.service';
import { MapPopupService } from '@connect-services/client/services/map-popup.service';
import { NetworkPathService } from '@connect-services/client/services/network-path/network-path.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { SubscriptionService } from '@connect-services/client/services/subscription.service';
import { ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { ExpansionPanelComponent } from '@connect-services/src/lib/components/expansion-panel/expansion-panel.component';
import {
    DepositsService,
    InformationSchemaTableExtended,
    NonEditableColumns,
    PermissionAliasEnum,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        EntityAttributesComponent,
        EntityAttributesInputComponent,
        EntityLinkDocumentListComponent,
        ExpansionPanelComponent,
        RelatedLayerListComponent,
        RessourcesModule,
        SynopticActionItemComponent,
    ],
    selector: 'app-entity-detail-template',
    templateUrl: './entity-detail-template.component.html',
})
export class EntityDetailTemplateComponent implements OnInit {
    public readonly attributesInformation = input.required<InformationSchemaTableExtended[]>();
    public readonly bookmarkedAttributes = input<AttributeValue[]>([]);
    public readonly businessIdentifierAttribute = signal<AttributeValue | undefined>(undefined);
    public readonly canReadDocuments = input<boolean>(false);
    public readonly centerMapOnEntityAndZoomIn = output<void>();
    public readonly columnsByName = input<Map<string, Column>>(new Map());
    public readonly dataEntity = input<EntityProperties>();
    public readonly documentData = input.required<DocumentIdentifiers>();
    public readonly editGeometryRequested = output<void>();
    public readonly exportOpticalPath = output<void>();
    public readonly geomBboxEntity = input.required<EntityLocation | null>();
    public readonly goToPreviousPage = output<void>();
    public readonly goToSynopticPanel = output<void>();
    public readonly hasBoxOrBayPlan = input<boolean>(false);
    public readonly hasOpticalPath = input<boolean>(false);
    public readonly hasSynoptic = input<boolean>(false);
    public readonly identifier = input<string>('');
    public readonly isEntityUsedInBreak = input<boolean>(false);
    public readonly notBookmarkedAttributes = input<AttributeValue[]>([]);
    public readonly openBoxOrBayPlan = output<void>();
    public readonly openBreakModal = output<void>();
    public readonly viewOpticalPath = output<void>();

    protected readonly availableInteractionCount = computed<number>((): number => {
        return [
            this.hasBoxOrBayPlan(),
            this.hasSynoptic(),
            this.hasOpticalPath(),
            this.isEntityUsedInBreak() && !this.editEntityDetailService.isEditMode(),
            this.networkPathService.canAccessNetworkPath() && this.hasOpticalPath(),
        ].filter((item: boolean) => item).length;
    });
    protected readonly businessBookmarkedAttributesForm = computed<FormControl<unknown> | undefined>(() => {
        const form = this.editEntityDetailService.bookmarkedAttributesForm();
        const businessIdentifierKey = this.editEntityDetailService.businessIdentifierKey();

        if (!form || !businessIdentifierKey || !form.controls[businessIdentifierKey]) {
            return undefined;
        }

        return form.controls[businessIdentifierKey];
    });
    protected readonly businessIdentifierLabel = computed<string>(() => {
        const name = this.businessIdentifierAttribute()?.key ?? '';

        return this.columnsByName().get(name)?.alias ?? name;
    });
    protected readonly businessIdentifierLabelTooltip = computed<string | undefined>(() => {
        const name = this.businessIdentifierAttribute()?.key;

        return name && this.columnsByName().get(name)?.alias ? name : undefined;
    });
    protected readonly canReadSynoptic = computed<boolean>(() => {
        return (
            !!this.subscription()?.options?.hasSynoptic &&
            this.hasPermissionsService.checkPermissions([PermissionAliasEnum.READ_SYNOPTIC])
        );
    });
    protected readonly columnNamesWithReference = signal<string[]>([]);
    protected readonly currentGeometry = signal<Geometry | null>(null);
    protected readonly entityId = computed<string | null>(() => {
        const dataEntity = this.dataEntity();

        if (!dataEntity) {
            return null;
        }

        return String(dataEntity.uuid);
    });
    protected readonly geometryEditButtonTooltip = computed<string>(() => {
        if (!!this.nonEditableColumns().find((column: NonEditableColumns) => column.name === 'geom')) {
            const column: NonEditableColumns | undefined = this.nonEditableColumns().find(
                (column: NonEditableColumns) => column.name === 'geom',
            );

            return column?.reasonForUnavailability ?? '';
        }

        if (!this.isLayerEditable()) {
            return 'editMode.layerStandardNotEditable';
        }

        if (!this.hasGeometry()) {
            return 'editMode.geometryEditionUnavailableForTabularEntity';
        }

        return '';
    });
    protected readonly hasGeometry = computed<boolean>(() => {
        const geom = this.dataEntity()?.geom as Geometry | undefined;

        return !!geom?.type;
    });
    protected readonly isDeleteButtonDisabled = computed(() => {
        return (
            !(this.featureService.data() ?? this.dataEntity()) || !this.mapEditModeService.editPermissions().canDelete
        );
    });
    protected readonly isEditModeActiveAndBusinessIdentifierEditable = computed(
        () =>
            this.editEntityDetailService.isEditMode() &&
            !!this.editEntityDetailService.businessIdentifierKey() &&
            !!this.businessIdentifierAttribute() &&
            this.editEntityDetailService.canEditBusinessIdentifier(),
    );
    protected readonly isEntityGeomEditActive = computed<boolean>(() => {
        const dataEntity = this.dataEntity();

        if (!dataEntity?.uuid) {
            return false;
        }

        return (
            this.mapEditModeService.isGeographicEditModeActive() &&
            this.mapEditModeService.editEntityId() === dataEntity.uuid &&
            this.mapEditModeService.activeEditLayerIds().has(dataEntity.layerId)
        );
    });
    protected readonly isFromStandardizeDeposit = signal<boolean>(false);
    protected readonly isGeometryEditButtonDisabled = computed<boolean>(() => {
        return (
            !this.editEntityDetailService.isEditMode() ||
            !this.isLayerEditable() ||
            !this.hasGeometry() ||
            this.nonEditableColumns().some((column: NonEditableColumns) => column.name === 'geom')
        );
    });
    protected readonly isLayerEditable = computed<boolean>(() =>
        this.projectEditModeService.editableLayerIds().includes(this.dataEntity()?.layerId ?? ''),
    );
    protected readonly isLayerRelated = computed<boolean>(
        () => !!this.layerRelations().length && !!this.editEntityDetailService.layer()?.tableName && !!this.entityId(),
    );
    protected readonly isMapPresentOnAnyPage = signal<boolean>(false);
    protected readonly layerRelations = signal<LayerRelation[]>([]);
    protected readonly nonEditableColumns = signal<NonEditableColumns[]>([]);
    protected readonly nonEditableColumnsName = computed<string[]>(() =>
        this.nonEditableColumns().map((nonEditableColumn: NonEditableColumns) => nonEditableColumn.name),
    );
    protected readonly nonEditableColumnsReasonByName = computed<Record<string, string | null>>(() =>
        this.nonEditableColumns().reduce(
            (accumulator: Record<string, string | null>, nonEditableColumn: NonEditableColumns) => {
                return { ...accumulator, [nonEditableColumn.name]: nonEditableColumn.reasonForUnavailability };
            },
            {},
        ),
    );

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly ButtonType = ButtonType;
    protected readonly contentLayoutService = inject(ContentLayoutService);
    protected readonly editEntityDetailService = inject(EditEntityDetailService);
    protected readonly featureService = inject(FeatureService);
    protected readonly mapEditModeService = inject(MapEditModeService);
    protected readonly mapPopupService = inject(MapPopupService);
    protected readonly networkPathService = inject(NetworkPathService);
    protected readonly RoutePathEnum = RoutePathEnum;

    private readonly broadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.VIEW);
    private readonly subscription = signal<ApiSubscription | undefined>(undefined);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly depositsService = inject(DepositsService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly editModeStateService = inject(EditModeStateService);
    private readonly entityEditPanelGuardService = inject(EntityEditPanelGuardService);
    private readonly geometryEditPreviewService = inject(GeometryEditPreviewService);
    private readonly hasPermissionsService = inject(HasPermissionsService);
    private readonly homeService = inject(HomeService);
    private readonly layersService = inject(LayersService);
    private readonly mapEditGeomService = inject(MapEditGeomService);
    private readonly mapHighlightEntityService = inject(MapHighlightEntityService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly router = inject(Router);
    private readonly sourcesService = inject(SourcesService);
    private readonly subscriptionService = inject(SubscriptionService);

    public constructor() {
        this.destroyRef.onDestroy(() => {
            this.editEntityDetailService.resetRecomputeError();

            this.clearGeometryPreview();
            if (!this.isEntityGeomEditActive()) {
                return;
            }

            this.mapEditModeService.toggleLayerEditMode([this.dataEntity()!.layerId], EditModeType.GEOGRAPHIC);
        });

        effect(() => {
            const isDataTableView: boolean = this.contentLayoutService.isDataTableView();
            this.isMapPresentOnAnyPage.set(!isDataTableView);
        });
    }

    public async ngOnInit(): Promise<void> {
        this.subscription.set(await this.subscriptionService.getSubscription());

        if (this.homeService.isExternalPage()) {
            this.broadcastChannel.postMessage(<BroadcastChannelEvent<{ isTableView: boolean }>>{
                message: BroadcastChannelMessageEnum.ASK_VIEW,
                source: BroadcastChannelSourceEnum.ENTITY_DETAIL,
            });

            this.broadcastChannel.onmessage = (
                event: MessageEvent<BroadcastChannelEvent<{ isTableView: boolean }>>,
            ): void => {
                if (
                    event.data.source !== BroadcastChannelSourceEnum.HOMEPAGE ||
                    event.data.message !== BroadcastChannelMessageEnum.CHANGE_VIEW ||
                    !event.data.additionalData
                ) {
                    return;
                }

                this.isMapPresentOnAnyPage.set(!event.data.additionalData.isTableView);
            };
        }

        this.businessIdentifierAttribute.set(
            this.bookmarkedAttributes().find(
                (bookmarkedAttribute) =>
                    bookmarkedAttribute.key === this.editEntityDetailService.businessIdentifierKey(),
            ) ??
                this.notBookmarkedAttributes().find(
                    (notBookmarkedAttribute) =>
                        notBookmarkedAttribute.key === this.editEntityDetailService.businessIdentifierKey(),
                ),
        );

        this.currentGeometry.set(this.getCurrentGeom());

        await this.setRelatedLayers();

        const layer = this.editEntityDetailService.layer();

        if (!this.editEntityDetailService.isEditMode() || !layer?.deposit || !layer?.tableName) {
            return;
        }

        try {
            const deposit = await firstValueFrom(
                this.depositsService.depositControllerGetDepositWithFolderAndStandard({
                    id: layer.deposit,
                }),
            );

            this.isFromStandardizeDeposit.set(!!deposit.standard);
            this.columnNamesWithReference.set(
                await this.editEntityDetailService.getColumnNamesWithReferences(deposit, layer.tableName),
            );

            this.nonEditableColumns.set(
                await this.editEntityDetailService.getNonEditableColumns(deposit, layer.tableName),
            );
        } catch {
            this.columnNamesWithReference.set([]);
            this.nonEditableColumns.set([]);
        }
    }

    protected async back(): Promise<void> {
        if (!(await this.entityEditPanelGuardService.confirmPanelLeaveIfNeeded())) {
            return;
        }

        this.clearGeometryPreview();
        this.goToPreviousPage.emit();
        this.mapPopupService.removeActiveElement();
    }

    protected async delete(): Promise<void> {
        const hasBeenDeleted = await this.mapEditModeService.requestDelete(
            this.dataEntity() ?? this.featureService.data() ?? null,
        );
        if (!hasBeenDeleted) {
            return;
        }
        this.goToPreviousPage.emit();
    }

    protected editEntityGeom(): void {
        const dataEntity = this.dataEntity();

        if (!dataEntity) {
            return;
        }

        let uuid: string | undefined = undefined;

        if (!this.isEntityGeomEditActive()) {
            const rawUuid = dataEntity.uuid;
            AssertService.isString(rawUuid);

            uuid = rawUuid;
            this.mapHighlightEntityService.resetHighlight();
            const type = this.editEntityDetailService.layer()?.geometryType ?? null;
            this.mapEditModeService.activeEditSelectionGeometryType.set(
                this.geometryTypeToActiveGeometryTypeEnum(type),
            );
        } else {
            this.highlightEntity();
            this.mapEditModeService.activeEditSelectionGeometryType.set(null);
        }
        this.currentGeometry.set(this.getCurrentGeom());

        this.mapEditModeService.toggleLayerEditMode([dataEntity.layerId], EditModeType.GEOGRAPHIC, uuid);
    }

    protected onGeometryEditButtonClick(): void {
        if (this.contentLayoutService.isDataTableView()) {
            this.editGeometryRequested.emit();

            return;
        }

        this.editEntityGeom();
    }

    protected recomputeEntityProperties(saveExtraObject: boolean = false): Promise<boolean> {
        const dataEntity = this.dataEntity();
        if (!dataEntity) {
            return Promise.resolve(false);
        }
        const currentGeom = this.getCurrentGeom();

        this.currentGeometry.set(currentGeom);

        return this.editEntityDetailService.recomputeEntityProperties(
            currentGeom,
            EditAction.UPDATE,
            null,
            this.isFromStandardizeDeposit(),
            saveExtraObject,
        );
    }

    protected async validateEdition(): Promise<void> {
        if (this.editEntityDetailService.areFormsOnError()) {
            return;
        }

        const geometryPreview = this.getGeometryPreview();

        if (geometryPreview) {
            this.editEntityDetailService.setPendingGeometryChange(
                geometryPreview.before as Geometry,
                geometryPreview.after as Geometry,
            );
        }

        const isRecomputeValid = await this.recomputeEntityProperties(true);

        if (!isRecomputeValid) {
            return;
        }

        this.editEntityDetailService.markAttributeFormsAsPristine();

        const projectId = this.projectBloc.value()?.id;

        if (!projectId) {
            return;
        }

        this.clearGeometryPreview();
        this.editModeStateService.notifyEditStateChanged(projectId);

        if (this.homeService.isExternalPage()) {
            this.goToPreviousPage.emit();

            return;
        }
        await this.router.navigate([RoutePathEnum.PROJECTS, projectId, RoutePathEnum.LAYERS]);
    }

    private clearGeometryPreview(): void {
        const dataEntity = this.dataEntity();

        if (!dataEntity?.uuid) {
            return;
        }

        AssertService.isString(dataEntity.uuid);
        const preview = this.geometryEditPreviewService.get(dataEntity.layerId, dataEntity.uuid);

        if (!preview) {
            return;
        }

        this.geometryEditPreviewService.clear(dataEntity.layerId, dataEntity.uuid);
        void this.redrawLayer(dataEntity.layerId);
    }

    private geometryTypeToActiveGeometryTypeEnum(
        geometryType: Layer.GeometryTypeEnum | null,
    ): ActiveGeometryTypeEnum | null {
        switch (geometryType) {
            case Layer.GeometryTypeEnum.POLYGON:
                return ActiveGeometryTypeEnum.FILL;
            case Layer.GeometryTypeEnum.LINE_STRING:
                return ActiveGeometryTypeEnum.LINE;
            case Layer.GeometryTypeEnum.POINT:
                return ActiveGeometryTypeEnum.SYMBOL;
            default:
                return null;
        }
    }

    private getCurrentGeom(): Geometry | null {
        const dataEntity = this.dataEntity();

        if (!dataEntity) {
            return null;
        }

        AssertService.isString(dataEntity.uuid);
        const previewGeometry = this.geometryEditPreviewService.getGeometry(dataEntity.layerId, dataEntity.uuid);
        if (previewGeometry) {
            return previewGeometry as Geometry;
        }

        const projectId = this.projectBloc.value()?.id;
        if (!projectId) {
            return null;
        }

        const entityState = this.editModeStateService.getEntityState(projectId, dataEntity.layerId, dataEntity.uuid);

        return entityState?.properties?.['geom'] || dataEntity.geom || null;
    }

    private getGeometryPreview(): GeometryEditPreview | undefined {
        const dataEntity = this.dataEntity();

        if (!dataEntity?.uuid) {
            return undefined;
        }

        AssertService.isString(dataEntity.uuid);

        return this.geometryEditPreviewService.get(dataEntity.layerId, dataEntity.uuid);
    }

    private highlightEntity(): void {
        const dataEntity = this.featureService.data();

        if (!dataEntity) {
            return;
        }

        const source = this.sourcesService.getSource(dataEntity.source);

        if (!source) {
            return;
        }

        this.mapHighlightEntityService.highlightMapEntity(dataEntity, source);
    }

    private async redrawLayer(layerId: string): Promise<void> {
        const source = this.sourcesService.getSource(layerId);

        if (!source) {
            return;
        }

        await this.mapEditGeomService.drawSource(source);
    }

    private async setRelatedLayers(): Promise<void> {
        const layerId = this.dataEntity()?.layerId;
        const projectId = this.projectBloc.value()?.id;

        if (!layerId || !projectId) {
            this.layerRelations.set([]);

            return;
        }

        try {
            this.layerRelations.set(
                await firstValueFrom(this.layersService.layerControllerGetRelatedLayers({ id: layerId, projectId })),
            );
        } catch {
            this.layerRelations.set([]);
        }
    }
}
